import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { SESSIONS, LANG_STORAGE_KEY } from "@/config/site";
import { getDictionary } from "@/i18n";
import { captureAdSource, captureRefCode } from "@/lib/ref";
import { publicApi } from "@/lib/api";

const LandingContext = createContext(null);

const readInitialLang = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl === "zh" || fromUrl === "en") return fromUrl;
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "zh" || stored === "en") return stored;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.warn("Language preference unavailable", error);
  }
  return "en";
};

const nextAvailableIndex = (sessions) => {
  const now = Date.now();
  let best = -1;
  let bestTime = Infinity;
  sessions.forEach((session, index) => {
    const time = new Date(session.iso).getTime();
    if (time > now && time < bestTime) {
      bestTime = time;
      best = index;
    }
  });
  return best >= 0 ? best : 0;
};

// `?session=YYYY-MM-DD` (used by cross-seminar invite emails) preselects that session.
const initialIndex = (sessions) => {
  try {
    const wanted = new URLSearchParams(window.location.search).get("session");
    if (wanted) {
      const index = sessions.findIndex((s) => s.iso.startsWith(wanted.slice(0, 10)));
      if (index >= 0) return index;
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "production") console.warn("Session preselect skipped", error);
  }
  return nextAvailableIndex(sessions);
};

export function LandingProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);
  const t = useMemo(() => getDictionary(lang), [lang]);
  const events = SESSIONS;
  const [selectedIndex, setSelectedIndex] = useState(() => initialIndex(SESSIONS));
  const [questionId, setQuestionId] = useState("");
  const [quickEmail, setQuickEmail] = useState("");
  const [progressive, setProgressive] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const refCode = useRef(null);
  const adSource = useRef({});
  // Adviser behind the ?ref= link (used to prefill "Invited by"); null when no/unknown code.
  const [refAdviser, setRefAdviser] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    refCode.current = captureRefCode();
    adSource.current = captureAdSource();
    if (!refCode.current) return undefined;
    let cancelled = false;
    publicApi
      .get(`/links/${encodeURIComponent(refCode.current)}`)
      .then(({ data }) => {
        if (!cancelled && data?.advisor_name) setRefAdviser({ code: data.code, name: data.advisor_name });
      })
      .catch(() => {
        // Unknown or paused code: keep the code for attribution, just no prefill.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.title = t.meta.title;
    const meta = document.querySelector("meta[name='description']");
    if (meta) meta.setAttribute("content", t.meta.description);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      const url = new URL(window.location.href);
      url.searchParams.set("lang", lang);
      window.history.replaceState(null, "", url);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.warn("Could not persist language preference", error);
    }
  }, [lang, t]);

  const setLang = useCallback(
    (next) => {
      if (next === lang) return;
      const apply = () => {
        setLangState(next);
        setQuestionId("");
      };
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!reduce && document.startViewTransition) document.startViewTransition(() => flushSync(apply));
      else apply();
    },
    [lang],
  );

  const scrollTo = useCallback((hash) => {
    const target = document.querySelector(hash);
    if (!target) return;
    if (lenisRef.current) lenisRef.current.scrollTo(target, { offset: -8 });
    else target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, []);

  const sessionTag = useCallback((event) => `${t.register.langTag[event.language]} · ${event.speaker}`, [t]);

  const value = useMemo(
    () => ({
      lang, setLang, t, events, selectedIndex, setSelectedIndex, selectedEvent: events[selectedIndex] || events[0], sessionTag,
      questionId, setQuestionId, quickEmail, setQuickEmail, progressive, setProgressive, refCode, refAdviser, adSource, lenisRef, scrollTo,
      registerOpen, setRegisterOpen,
    }),
    [lang, setLang, t, events, selectedIndex, sessionTag, questionId, quickEmail, progressive, scrollTo, registerOpen, refAdviser],
  );

  return <LandingContext.Provider value={value}>{children}</LandingContext.Provider>;
}

export const useLanding = () => useContext(LandingContext);
